// The more (•••) menu: secondary actions that stay out of the way until asked for (progressive disclosure). Phase 2
// puts "Change thumbnail" in it on the project screen. Closes on Escape, a tap outside, or picking an item.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "./bits";

export type MoreItem = { id: string; label: string; hint?: string; icon?: string; disabled?: boolean; onSelect: () => void; /** v4 (D6): the tap's parity id (web/src/parity.ts) */ dataAction?: string };

/** `trigger`: a labelled button ("Make…") instead of the ••• icon, with its own class; `menuClass` adds to the menu's. */
export function MoreMenu({ label = "More", items, trigger, triggerClass, menuClass }: { label?: string; items: MoreItem[]; trigger?: ReactNode; triggerClass?: string; menuClass?: string }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    wrap.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
    const outside = (e: PointerEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        wrap.current?.querySelector<HTMLButtonElement>(".more-btn")?.focus();
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const all = [...(wrap.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? [])];
      const i = all.indexOf(document.activeElement as HTMLButtonElement);
      all[(i + (e.key === "ArrowDown" ? 1 : all.length - 1)) % all.length]?.focus();
      e.preventDefault();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  return (
    <div className="more-wrap" ref={wrap}>
      {trigger ? (
        <button type="button" className={`${triggerClass ?? "btn btn-quiet"} more-btn${open ? " is-on" : ""}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
          {trigger}
        </button>
      ) : (
        <button type="button" className={`icon-btn more-btn${open ? " is-on" : ""}`} aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
          <Icon name="more" />
        </button>
      )}
      {open && (
        <div className={`more-menu${menuClass ? ` ${menuClass}` : ""}`} role="menu" aria-label={label}>
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              role="menuitem"
              className="more-item"
              data-action={it.dataAction}
              disabled={it.disabled}
              onClick={() => {
                setOpen(false);
                it.onSelect();
              }}
            >
              {it.icon && <Icon name={it.icon} size={18} />}
              <span>
                <span className="more-item-label">{it.label}</span>
                {it.hint && <span className="more-item-hint">{it.hint}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
